import { CustomerAccountService } from '@amzn/awscat-portfolio-assessment-service-client';
import { CustomerAccount } from '@amzn/awscat-portfolio-assessment-types';
import { Box, Button, Form, Grid, Input, Link, SpaceBetween, Spinner } from '@amzn/awsui-components-react';
import { useEffect, useState } from 'react';
import { useIsMounted } from 'react-tidy';

import { constants } from '../../common/Localization';
import AWSCATTable from '../../common/table/Table-index';
import { ColumnDefinition, ColumnTypes } from '../../common/table/types';
import { emptyAccount, maxCustomerAccounts, salesforceCreateAccountUrl } from './constants';
interface Props {
    setAccount: (account: CustomerAccount) => void;
    account: CustomerAccount;
    hidePanel: () => void;
    visible: boolean;
}

const customerAccountService: CustomerAccountService = new CustomerAccountService();
const CustomerAccountPanel = (props: Props) => {
    const [ searchTerm, setSearchTerm ] = useState('');
    const [ accountItems, setAccountItems ] = useState<CustomerAccount[]>([]);
    const [ loading, setLoading ] = useState(false);
    const [ showTable, setShowTable ] = useState(false); //don't show table while user is typing the first search term
    const isMounted = useIsMounted();

    useEffect(() => {
        setSearchTerm('');
        setAccountItems([]);
        setShowTable(false);
    }, [ props.visible ]);

    const listAccount = async () => {
        if (!searchTerm) {
            return;
        }
        setShowTable(true);
        setLoading(true);
        const accounts: CustomerAccount[] = await customerAccountService.list(maxCustomerAccounts, searchTerm);
        if (isMounted()) {
            setAccountItems(accounts);
            setLoading(false);
        }
    };

    const onSelectChange = (items?: Readonly<CustomerAccount[]>) => {
        props.setAccount(items?.[0] || emptyAccount);
    };

    const getResultsContent = (): JSX.Element | null => {
        if (loading) {
            return (<Spinner size='large'/>);
        } else if (!showTable) {
            return null;
        } else {
            return (
                <AWSCATTable
                    loading={loading}
                    resourceName={constants.SEARCH_ACCOUNT_TABLE_HEADER}
                    items={accountItems}
                    columnDefinitions={colDef}
                    onSelectionChange={onSelectChange}
                    selectionType='single'
                    emptyTable={
                        <Box textAlign='center' data-testid='emptyTable'>
                            <Box padding={{ bottom: 's' }} variant='p'>
                                {constants.CUSTOMER_ACCOUNT_NOT_FOUND}
                            </Box>
                        </Box>
                    }
                />
            );
        }
    };

    return (
        <form onSubmit={e => e.preventDefault()}>
            <Form
                actions={
                    <SpaceBetween direction="horizontal" size="xs">
                        <Button variant="link" loading={loading} onClick={() => props.hidePanel()}>{constants.CANCEL_BUTTON_TEXT}</Button>
                        <Button variant="primary" disabled={!props.account.accountId}
                            loading={loading} onClick={() => props.hidePanel()}>{constants.CONFIRM_BUTTON_TEXT}</Button>
                    </SpaceBetween>
                }
            >
                <SpaceBetween size='s'>
                    <h2>{constants.SELECT_CUSTOMER_ACCOUNT}</h2>
                    <div>{constants.SELECT_CUSTOMER_ACCOUNT_HINT1}</div>
                    <div>{constants.SELECT_CUSTOMER_ACCOUNT_HINT2}</div>
                    <div>{constants.SELECT_CUSTOMER_ACCOUNT_HINT3}
                        <Link target='_blank' rel='noopener noreferrer' href={salesforceCreateAccountUrl}> Salesforce.com</Link>.
                    </div>
                    <Grid gridDefinition={[{colspan: 10}, {colspan: 2}]}>
                        <Input
                            data-testid='searchInput'
                            value={searchTerm}
                            placeholder={constants.SEARCH_ACCOUNT_PLACEHOLDER}
                            onChange={event => setSearchTerm(event.detail.value)}
                            type='search'
                        />
                        <Button
                            data-testid='searchBtn'
                            onClick={listAccount}
                            variant='primary'
                            disabled={!searchTerm}
                            loading={loading}
                        >
                            {constants.SEARCH}
                        </Button>
                    </Grid>
                    {getResultsContent()}
                </SpaceBetween>
            </Form>
        </form>
    );
};

const colDef: ColumnDefinition<CustomerAccount>[] = [
    {
        id: 'accountName',
        header: constants.ACCOUNT_NAME,
        cell: item => item.accountName,
        type: ColumnTypes.TEXT,
    },
    {
        id: 'accountId',
        header: constants.ACCOUNT_ID,
        cell: item => item.accountId,
        type: ColumnTypes.TEXT,
    },
    {
        id: 'ownerId',
        header: constants.ACCOUNT_OWNER,
        cell: item => item.ownerId,
        type: ColumnTypes.TEXT,
    },
    {
        id: 'territory',
        header: constants.ACCOUNT_TERRITORY,
        cell: item => item.territory,
        type: ColumnTypes.TEXT,
    },
];

export default CustomerAccountPanel;
